import { useCallback, useEffect, useRef, useState } from 'react';
import izenuAPI from '../../services/izenuAPI';

// Platform settings (admin). Today: the ISRC generator, from api/admin/isrc/.
// New platform-wide settings go here as their own <Section>.

const inputCls = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100 disabled:text-gray-500';
const btnPrimary = 'rounded-lg bg-indigo-500 px-4 py-2 text-sm font-medium !text-white hover:bg-indigo-600 disabled:opacity-50';

function Field({ label, hint, error, wide, children }) {
    return (
        <label className={`block ${wide ? 'sm:col-span-2' : ''}`}>
            <span className="mb-1 block text-sm font-medium text-gray-700">{label}</span>
            {children}
            {error
                ? <span className="mt-1 block text-xs text-red-600">{error}</span>
                : hint ? <span className="mt-1 block text-xs text-gray-500">{hint}</span> : null}
        </label>
    );
}

function Section({ title, subtitle, aside, children }) {
    return (
        <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                    {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
                </div>
                {aside}
            </div>
            {children}
        </section>
    );
}

function Toggle({ checked, onChange, label, hint, disabled }) {
    return (
        <label className={`flex items-start gap-3 rounded-lg border border-gray-200 p-3 ${disabled ? 'opacity-60' : 'cursor-pointer hover:bg-gray-50'}`}>
            <input type="checkbox" className="sr-only peer" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
            <span aria-hidden="true" className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition ${checked ? 'bg-indigo-500' : 'bg-gray-300'} peer-focus-visible:ring-2 peer-focus-visible:ring-indigo-400`}>
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`} />
            </span>
            <span>
                <span className="block text-sm font-medium text-gray-800">{label}</span>
                {hint && <span className="block text-xs text-gray-500">{hint}</span>}
            </span>
        </label>
    );
}

function Stat({ label, value, hint }) {
    return (
        <div className="rounded-lg border border-gray-200 p-3">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-xl font-semibold text-gray-900 tabular-nums">{value}</div>
            {hint && <div className="text-xs text-gray-400">{hint}</div>}
        </div>
    );
}

const formatDate = (s) => s ? new Date(s.replace(' ', 'T')).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

export default function AdminSettings() {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [isrc, setIsrc] = useState(null);
    const [isrcForm, setIsrcForm] = useState({});
    const [isrcMsg, setIsrcMsg] = useState(null);
    const [savingIsrc, setSavingIsrc] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const { data } = await izenuAPI.get('/admin/isrc/fetch.php');
            if (!data.response) throw new Error(data.message);
            setIsrc(data.isrc);
            if (data.isrc?.installed) setIsrcForm(isrcFormValues(data.isrc));
        } catch (e) {
            console.log(e);
            setLoadError('Não foi possível carregar as definições.');
        }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const isrcChanges = isrc?.installed
        ? Object.fromEntries(Object.keys(isrcForm).filter(k => String(isrcForm[k]) !== String(isrcFormValues(isrc)[k])).map(k => [k, isrcForm[k]]))
        : {};
    const isrcDirty = Object.keys(isrcChanges).length > 0;
    const prefixValid = /^[A-Z]{2}$/.test(isrcForm.country || '') && /^[A-Z0-9]{3}$/.test(isrcForm.registrant || '');

    const saveIsrc = async (e) => {
        e.preventDefault();
        if (!isrcDirty || !prefixValid) return;
        const turningOn = isrcForm.enabled && !isrc.enabled;
        if (turningOn && !isrcForm.official && !window.confirm(
            `Vais ligar o gerador com um código provisório (${isrcForm.country}-${isrcForm.registrant}).\n\n`
            + 'Os códigos emitidos ficam visíveis aos artistas e são permanentes — se forem usados noutras lojas, '
            + 'já não se trocam. Ficam marcados como provisórios para poderem ser reemitidos.\n\nLigar mesmo assim?'
        )) return;
        setSavingIsrc(true);
        setIsrcMsg(null);
        try {
            const { data } = await izenuAPI.post('/admin/isrc/update.php', isrcChanges);
            if (data.response) {
                setIsrc(data.isrc);
                setIsrcForm(isrcFormValues(data.isrc));
                setIsrcMsg({ ok: true, text: 'Configuração de ISRC guardada.' });
            } else {
                setIsrcMsg({ ok: false, text: data.message || 'Não foi possível guardar.', field: data.field });
            }
        } catch (err) {
            console.log(err);
            setIsrcMsg({ ok: false, text: err.response?.data?.message || 'Não foi possível guardar.' });
        }
        setSavingIsrc(false);
    };

    const setI = (k, v) => setIsrcForm(prev => ({ ...prev, [k]: v }));

    if (loading) {
        return <div className="mx-auto max-w-4xl p-3 sm:p-6 text-left text-sm text-gray-500">A carregar…</div>;
    }
    if (loadError) {
        return (
            <div className="mx-auto max-w-4xl p-3 sm:p-6 text-left">
                <p className="text-sm text-red-600">{loadError}</p>
                <button type="button" onClick={load} className={`${btnPrimary} mt-3`}>Tentar novamente</button>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-4xl p-3 sm:p-6 text-left space-y-6">
            <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Definições</h1>
                <p className="text-sm text-gray-500">Configurações da plataforma.</p>
            </div>

            <SalesSection />
            <CampaignsSection />

            <StudioInsightsSection />

            <Section
                    title="Gerador de ISRC"
                    subtitle="Códigos atribuídos pela Izenu às músicas que ainda não foram lançadas noutras plataformas."
                    aside={isrc.installed && <StatusPill isrc={isrc} />}
                >
                    {!isrc.installed ? (
                        <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
                            O gerador ainda não está instalado no servidor (<code>bin/migration_isrc_generator.php</code>).
                        </p>
                    ) : (
                        <form onSubmit={saveIsrc} className="space-y-5">
                            {!isrc.official && (
                                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                                    <strong>Código provisório.</strong> A Izenu ainda não tem código de registante da agência de ISRC.
                                    Códigos emitidos agora ficam marcados como provisórios — se a agência atribuir outro código,
                                    têm de ser reemitidos. Os relatórios já gerados não mudam.
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                <Field label="País" hint="2 letras" error={isrcMsg?.field === 'country' ? isrcMsg.text : undefined}>
                                    <input className={`${inputCls} font-mono uppercase`} maxLength={2} value={isrcForm.country}
                                        onChange={e => setI('country', e.target.value.toUpperCase().replace(/[^A-Z]/g, ''))} />
                                </Field>
                                <Field label="Código de registante" hint="3 letras ou números, atribuído pela agência"
                                    error={isrcMsg?.field === 'registrant' ? isrcMsg.text : undefined}>
                                    <input className={`${inputCls} font-mono uppercase`} maxLength={3} value={isrcForm.registrant}
                                        onChange={e => setI('registrant', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} />
                                </Field>
                                <Field label="Próximo código" hint={isrcDirty ? 'Com o prefixo actual guardado' : 'Ano da atribuição + sequência'}>
                                    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-800">
                                        {isrc.next_isrc}
                                    </div>
                                </Field>
                            </div>

                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                <Toggle
                                    checked={isrcForm.enabled}
                                    onChange={v => setI('enabled', v)}
                                    label="Gerador activo"
                                    hint="Ligado, as músicas aprovadas que pediram ISRC recebem um código automaticamente."
                                />
                                <Toggle
                                    checked={isrcForm.official}
                                    onChange={v => setI('official', v)}
                                    label="Código oficial da agência"
                                    hint="Marca quando o código de registante for o atribuído oficialmente."
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                                <Stat label="Prontas a receber" value={isrc.stats.pending_ready} hint="aprovadas, à espera" />
                                <Stat label="Em revisão" value={isrc.stats.pending_review} hint="recebem após aprovação" />
                                <Stat label="Emitidos" value={isrc.stats.issued}
                                    hint={isrc.stats.last_isrc ? `último ${isrc.stats.last_isrc}` : 'nenhum ainda'} />
                                <Stat label="Provisórios" value={isrc.stats.provisional} hint="a reemitir com o código oficial" />
                            </div>

                            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
                                <span className="text-xs sm:me-auto">
                                    {isrcMsg && !isrcMsg.field
                                        ? <span className={isrcMsg.ok ? 'text-green-600' : 'text-red-600'}>{isrcMsg.text}</span>
                                        : <span className="text-gray-500">
                                            Mudar o prefixo só afecta os códigos emitidos daqui em diante.
                                            {isrc.updated_at ? ` Alterado a ${formatDate(isrc.updated_at)}.` : ''}
                                        </span>}
                                </span>
                                <button type="submit" disabled={!isrcDirty || !prefixValid || savingIsrc} className={btnPrimary}>
                                    {savingIsrc ? 'A guardar…' : 'Guardar ISRC'}
                                </button>
                            </div>
                        </form>
                    )}
            </Section>
        </div>
    );
}

function isrcFormValues(isrc) {
    return {
        country: isrc.country || '',
        registrant: isrc.registrant || '',
        enabled: Boolean(isrc.enabled),
        official: Boolean(isrc.official),
    };
}

function StatusPill({ isrc }) {
    const [label, cls] = isrc.active
        ? [isrc.official ? 'Activo' : 'Activo · provisório', isrc.official ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800']
        : ['Desligado', 'bg-gray-100 text-gray-600'];
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${cls}`}>
            <span className={`h-2 w-2 rounded-full ${isrc.active ? (isrc.official ? 'bg-green-500' : 'bg-amber-500') : 'bg-gray-400'}`} />
            {label}
        </span>
    );
}

const CDN = 'https://dpagz47n4g1lw.cloudfront.net';
const avatarUrl = (a) => (!a.photo ? '' : a.photo.startsWith('http') ? a.photo : `${CDN}/users/${a.user_id}/profile_photos/${a.photo}`);

/**
 * Who sees the studio's "Visão geral" and "Vendas e reproduções" (web app, and
 * the same tabs on the distributor panel). Selected artists, or everyone with
 * one switch; turning it off again keeps the selection. api/admin/studio_insights/.
 */
function StudioInsightsSection() {
    const [state, setState] = useState(null);   // { installed, all, artists, artist_count }
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [query, setQuery] = useState('');
    const [hits, setHits] = useState([]);
    const timer = useRef(null);

    useEffect(() => {
        izenuAPI.get('/admin/studio_insights/fetch.php')
            .then(({ data }) => setState(data))
            .catch(() => setError('Não foi possível carregar.'));
        return () => clearTimeout(timer.current);
    }, []);

    const update = async (body) => {
        setBusy(true); setError('');
        try {
            const { data } = await izenuAPI.post('/admin/studio_insights/update.php', body);
            setState((s) => ({ ...s, all: data.all, artists: data.artists }));
        } catch (err) {
            setError(err?.response?.data?.message || 'Não foi possível guardar.');
        }
        setBusy(false);
    };

    const search = (v) => {
        setQuery(v);
        clearTimeout(timer.current);
        if (v.trim().length < 2) { setHits([]); return; }
        timer.current = setTimeout(() => {
            izenuAPI.get('/artist/lookup.php', { params: { q: v.trim() } })
                .then(({ data }) => setHits(Array.isArray(data?.data) ? data.data : []))
                .catch(() => setHits([]));
        }, 250);
    };

    const selectedIds = new Set((state?.artists || []).map((a) => a.user_id));

    return (
        <Section
            title="Estatísticas no Estúdio"
            subtitle="Quem vê as secções Visão geral e Vendas e reproduções — no Estúdio do web app e nas abas do artista no painel do distribuidor. Os Dados bancários ficam disponíveis para todos."
            aside={state?.installed && (
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${state.all ? 'bg-green-100 text-green-700' : 'bg-indigo-100 text-indigo-700'}`}>
                    <span className={`h-2 w-2 rounded-full ${state.all ? 'bg-green-500' : 'bg-indigo-500'}`} />
                    {state.all ? 'Todos os artistas' : `${state.artists.length} ${state.artists.length === 1 ? 'artista' : 'artistas'}`}
                </span>
            )}
        >
            {!state ? (
                <p className="text-sm text-gray-500">{error || 'A carregar…'}</p>
            ) : !state.installed ? (
                <p className="text-sm text-amber-700">Ainda não instalado: falta correr <code>bin/migration_studio_insights.php</code>. Até lá, todos os artistas vêem as secções.</p>
            ) : (
                <div className="space-y-4">
                    <Toggle
                        checked={!!state.all}
                        disabled={busy}
                        onChange={(on) => update({ all: on })}
                        label="Liberar para todos os artistas"
                        hint={state.all
                            ? `Os ${state.artist_count} artistas vêem as secções. A lista abaixo fica guardada para quando desligares.`
                            : 'Desligado: só os artistas da lista abaixo vêem as secções.'}
                    />

                    <div className={state.all ? 'opacity-50' : ''}>
                        <p className="mb-2 text-sm font-medium text-gray-700">Artistas com acesso</p>
                        <div className="relative mb-3 max-w-md">
                            <input className={inputCls} value={query} onChange={(e) => search(e.target.value)} disabled={busy}
                                placeholder="Procurar artista para dar acesso…" aria-label="Procurar artista" />
                            {hits.length > 0 && (
                                <ul className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-lg !border !border-solid border-gray-200 bg-white shadow-lg [&_li]:!p-0">
                                    {hits.map((h) => (
                                        <li key={h.user_id}>
                                            <button type="button" disabled={selectedIds.has(h.user_id)}
                                                onClick={() => { update({ artist_id: h.user_id, enabled: true }); setQuery(''); setHits([]); }}
                                                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-900 hover:bg-indigo-50 disabled:opacity-50">
                                                {avatarUrl(h) ? <img src={avatarUrl(h)} alt="" className="h-6 w-6 rounded-full object-cover" /> : <span className="h-6 w-6 rounded-full bg-gray-100" />}
                                                <span className="flex-1 truncate">{h.name}</span>
                                                <span className="text-xs text-gray-400">{selectedIds.has(h.user_id) ? 'Já tem acesso' : 'Dar acesso'}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        {state.artists.length === 0 ? (
                            <p className="text-sm text-gray-500">Nenhum artista seleccionado.</p>
                        ) : (
                            <ul className="divide-y divide-gray-100 rounded-lg !border !border-solid border-gray-200 [&>li]:!p-0">
                                {state.artists.map((a) => (
                                    <li key={a.user_id}>
                                        <div className="flex items-center gap-3 px-3 py-2">
                                            {avatarUrl(a) ? <img src={avatarUrl(a)} alt="" className="h-8 w-8 rounded-full object-cover" /> : <span className="h-8 w-8 rounded-full bg-gray-100" />}
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-medium text-gray-900">{a.name}</span>
                                                <span className="block truncate text-xs text-gray-500">{a.distributor_name || 'Sem distribuidora'}</span>
                                            </span>
                                            <button type="button" disabled={busy} onClick={() => update({ artist_id: a.user_id, enabled: false })}
                                                className="rounded-lg px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50">Retirar</button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {error && <p className="text-sm text-red-600">{error}</p>}
                </div>
            )}
        </Section>
    );
}

/**
 * What the mobile apps offer to buy, per platform. The web always sells and
 * ignores these. Off only changes what the app shows — the server does not
 * refuse payments. api/admin/sales/switches.php, classes/SalesSwitch.php.
 */
const SALE_PLATFORMS = [
    { id: 'android', label: 'App Android' },
    { id: 'ios', label: 'App iOS (iPhone)' },
];
const SALE_KINDS = [
    { id: 'releases', label: 'Venda de lançamentos', on: 'mostra o preço e "Comprar na Web"', off: 'mostra "Álbum exclusivo", sem preço nem botão' },
    { id: 'premium_link', label: 'Link para assinar no site', on: 'por baixo do pagamento pela loja, mostra um link discreto para assinar em app.izenu.ao (Multicaixa)', off: 'só o pagamento pela loja (Google Play / Apple), como até agora' },
];
// Showing a price or a way to pay outside the store breaks the store's rules.
const STORE_WARNING = {
    'releases:android': '⚠️ Ligado viola a política de pagamentos do Google Play (a app foi sinalizada por isto a 2026-10-05) e pode levar à remoção da app.',
    'releases:ios': '⚠️ "Comprar na Web" também é contra a regra 3.1.1 da Apple fora dos EUA (até agora passou).',
    'premium_link:android': '⚠️ Ligado viola a política de pagamentos do Google Play — a app foi sinalizada a 2026-10-05 precisamente pela assinatura por Multicaixa — e pode levar à remoção da app.',
    'premium_link:ios': '⚠️ Um link para pagar fora da App Store é contra a regra 3.1.1 da Apple fora dos EUA e pode levar à rejeição ou remoção da app.',
};

function SalesSection() {
    const [sales, setSales] = useState(null);   // { android: { releases, subscription }, ios: {…} }
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        izenuAPI.get('/admin/sales/switches.php')
            .then(({ data }) => setSales(data.sales || {}))
            .catch(() => setError('Não foi possível carregar.'));
    }, []);

    const toggle = async (platform, kind, on) => {
        const warning = on ? STORE_WARNING[`${kind}:${platform}`] : null;
        if (warning && !window.confirm(`${warning}\n\nLigar mesmo assim?`)) return;
        setBusy(true); setError('');
        try {
            const { data } = await izenuAPI.post('/admin/sales/switches.php', { kind, platform, enabled: on });
            setSales(data.sales);
        } catch (err) {
            setError(err?.response?.data?.message || 'Não foi possível guardar.');
        }
        setBusy(false);
    };

    return (
        <Section
            title="Vendas nas apps"
            subtitle="O que as apps Android e iOS mostram para comprar. O site (app.izenu.ao) vende sempre e não é afectado. Desligar só muda o que a app mostra — não bloqueia pagamentos já abertos, e quem comprou continua com acesso. A app lê isto ao abrir."
        >
            {!sales ? (
                <p className="text-sm text-gray-500">{error || 'A carregar…'}</p>
            ) : (
                <div className="grid gap-5 sm:grid-cols-2">
                    {SALE_PLATFORMS.map((p) => (
                        <div key={p.id} className="space-y-3">
                            <p className="text-sm font-semibold text-gray-800">{p.label}</p>
                            {SALE_KINDS.map((k) => {
                                const on = !!sales[p.id]?.[k.id];
                                const warning = STORE_WARNING[`${k.id}:${p.id}`];
                                return (
                                    <Toggle
                                        key={k.id}
                                        checked={on}
                                        disabled={busy}
                                        onChange={(v) => toggle(p.id, k.id, v)}
                                        label={`${k.label} — ${on ? 'ligada' : 'desligada'}`}
                                        hint={`${on ? k.on : k.off}.${warning ? ' ' + warning : ''}`}
                                    />
                                );
                            })}
                        </div>
                    ))}
                    {error && <p className="text-sm text-red-600 sm:col-span-2">{error}</p>}
                </div>
            )}
        </Section>
    );
}

/**
 * Campaign landing pages (app.izenu.ao/100mil). Closed, the page says the
 * campaign has ended and takes no new pre-registrations; people who already
 * registered stay in it. api/admin/campaign/{switches,setOpen}.php.
 */
const CAMPAIGNS = [
    { id: '100mil', label: 'A tua música pode valer 100.000 Kz', url: 'app.izenu.ao/100mil' },
];

function CampaignsSection() {
    const [open, setOpen] = useState(null);   // { '100mil': bool }
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        izenuAPI.get('/admin/campaign/switches.php')
            .then(({ data }) => setOpen(data.campaigns || {}))
            .catch(() => setError('Não foi possível carregar.'));
    }, []);

    const toggle = async (campaign, on) => {
        if (!on && !window.confirm(
            'Fechar a campanha?\n\nA página passa a dizer que a campanha terminou e deixa de aceitar pré-inscrições. '
            + 'Quem já se inscreveu continua na campanha. Podes reabrir a qualquer momento.'
        )) return;
        setBusy(true); setError('');
        try {
            const { data } = await izenuAPI.post('/admin/campaign/setOpen.php', { campaign, open: on });
            setOpen(data.campaigns);
        } catch (err) {
            setError(err?.response?.data?.message || 'Não foi possível guardar.');
        }
        setBusy(false);
    };

    return (
        <Section
            title="Campanhas"
            subtitle="Páginas de campanha. Fechada, a página diz que a campanha terminou e deixa de aceitar pré-inscrições; quem já se inscreveu continua na campanha. A mudança chega à página em até 1 minuto."
        >
            {!open ? (
                <p className="text-sm text-gray-500">{error || 'A carregar…'}</p>
            ) : (
                <div className="space-y-3">
                    {CAMPAIGNS.map((c) => (
                        <Toggle
                            key={c.id}
                            checked={!!open[c.id]}
                            disabled={busy}
                            onChange={(on) => toggle(c.id, on)}
                            label={`${c.label} — ${open[c.id] ? 'aberta' : 'fechada'}`}
                            hint={c.url}
                        />
                    ))}
                    {error && <p className="text-sm text-red-600">{error}</p>}
                </div>
            )}
        </Section>
    );
}
